import { useQuery } from "@tanstack/solid-query";
import { X } from "lucide-solid";
import { createContext, createSignal, For, onCleanup, onMount } from "solid-js";
import { createStore, produce, unwrap } from "solid-js/store";
import { twJoin } from "tailwind-merge";
import Editor from "~/js/editor";
import type { GraphNode as NodeType } from "~/js/graph/node-socket-connection";
import { NodeEvents } from "~/js/types/IEventorListners";
import type { StyleData } from "~/js/utilities/components/styleParam";
import EventAnalytics from "~/js/utilities/EventAnalytics";
import { Button, DebugFlags, isDebugFlag, useCustomContext } from "~/solidJs/shared";
import { type IStyleItem, StylesApi } from "~/solidJs/shared/api/StylesApi";
import { findSocket } from "~/solidJs/shared/helpers/nodeHelpers";
import type { Coordinates } from "~/solidJs/shared/helpers/types/ISizeble";
import { StyleListItem } from "./StyleItem";

/**
 * Represents a node style item.
 */
type INodeStyleItem = {
	nodeUUID: string;
	globalId: string;
	selectedStyle?: IStyleItem;
};

/**
 * Represents a store for node styles.
 */
type NodeStylesStore = Record<string, INodeStyleItem>;

const [getStyleItems, setStyleItems] = createStore<NodeStylesStore>({});

/**
 * Adds a style item to the store.
 * @param props - The node style item to add.
 */
const addStyleItem = (props: INodeStyleItem) => {
	setStyleItems(
		produce((state) => {
			if (props.nodeUUID in state) {
				return;
			}
			state[props.nodeUUID] = props;
		})
	);
};

/**
 * Checks if a style is selected for a given node.
 * @param props - The node UUID and style value to check.
 * @returns True if the style is selected, false otherwise.
 */
const getIsStyleSelected = (
	props: Pick<INodeStyleItem, "nodeUUID"> & Pick<IStyleItem, "value">
) => {
	const nodeWidget = getStyleItems[props.nodeUUID];
	if (!nodeWidget.selectedStyle) {
		return false;
	}
	return nodeWidget.selectedStyle.value === props.value;
};

/**
 * Gets the selected style for a given node.
 * @param props - The node UUID to get the selected style for.
 * @returns The selected style item or undefined.
 */
const getSelectedStyle = (props: Pick<INodeStyleItem, "nodeUUID">) => {
	return getStyleItems[props.nodeUUID].selectedStyle;
};

/**
 * Checks if the styles should be opened for the first time in the session.
 * @param node - The node to check.
 * @returns True if the styles should be opened, false otherwise.
 */
const isNeedToOpenFirstInSessionStyles = (node: NodeType) => {
	if (isDebugFlag(DebugFlags.ModalsDebug)) {
		return false;
	}
	const out =
		node.isStylesShowable &&
		!Editor.context.graph.isStylesWereOppen &&
		node.globalId === "Phygital Creator/phygc-rnd-comfy-sdxl-text2img";
	if (out) {
		Editor.context.graph.isStylesWereOppen = true;
		return out;
	}
	return out;
};

/**
 * Deletes a style item from the store.
 * @param props - The node UUID to delete the style item for.
 */
const deleteItem = (props: Pick<INodeStyleItem, "nodeUUID">) => {
	const node = Editor.context.graph.nodesHashMap.get(props.nodeUUID);
	if (node) {
		node.isStyleOpen = false;
	}
	setStyleItems(
		produce((state) => {
			delete state[props.nodeUUID];
		})
	);
};

/**
 * Selects a style for a given node.
 * @param props - The node UUID and style to select.
 */
const selectStyle = (props: Pick<INodeStyleItem, "nodeUUID"> & { style: IStyleItem }) => {
	setStyleItems(
		produce((state) => {
			if (!state[props.nodeUUID]) {
				return;
			}
			state[props.nodeUUID].selectedStyle = props.style;
		})
	);
};

/**
 * Applies a style to a given node.
 * @param props - The node UUID and style to apply.
 */
const applyStyle = (props: Pick<INodeStyleItem, "nodeUUID"> & { style: IStyleItem }) => {
	const node = Editor.context.graph.nodesHashMap.get(props.nodeUUID);
	if (!node) {
		return;
	}
	const styleSocket = findSocket("style", node);
	if (!styleSocket) {
		return;
	}
	const data: StyleData = {
		style: unwrap(props.style)
	};
	styleSocket.setValue(data);
};

/**
 * Component that renders the node style widget.
 */
export const NodeStyleWidget = () => {
	return <For each={Object.values(getStyleItems)}>{(item) => <NodeStyleItem {...item} />}</For>;
};

export const StyleContext = createContext<INodeStyleItem>();
const STYLE_LEFT_PADDING = 50;

/**
 * Component that renders a single node style item.
 * @param props - The node style item properties.
 */
const NodeStyleItem = (props: INodeStyleItem) => {
	const [getPos, setPos] = createSignal<Coordinates>({
		left: 0,
		top: 0
	});

	let nodeWidth = 350;

	const onNodePositionChange = (pos: Coordinates) => {
		setPos(pos);
	};

	const onNodeRemoval = () => {
		deleteItem(props);
	};

	const getNodeInitSizes = (node: NodeType) => {
		nodeWidth = node.layout.getNodeWidth();
		const initPos = node.layout.getPosition();
		onNodePositionChange({ left: initPos.x, top: initPos.y });
	};

	const initNodeListners = (node: NodeType) => {
		node.layout.addListener("position", onNodePositionChange);
		node.addListener(NodeEvents.Remove, onNodeRemoval);
	};

	const removeNodeListners = (node: NodeType) => {
		node.layout.removeListener("position", onNodePositionChange);
		node.removeListener("remove", onNodeRemoval);
	};

	onMount(() => {
		const node = Editor.context.graph.nodesHashMap.get(props.nodeUUID);
		if (!node) {
			return;
		}
		initNodeListners(node);
		getNodeInitSizes(node);

		onCleanup(() => {
			removeNodeListners(node);
		});
	});

	const styles = useQuery(() => ({
		queryKey: [StylesApi.key_StyleList, props.globalId],
		queryFn: () => StylesApi.getStylesList(props.globalId)
	}));

	return (
		<StyleContext.Provider value={props}>
			<div
				class="pointer-events-auto absolute flex flex-col gap-4 rounded-3xl bg-white p-6"
				style={{
					"box-shadow": "-19px 24px 80px 0 rgba(0, 0, 0, 0.10)",
					transform: `translate(${getPos().left + nodeWidth + STYLE_LEFT_PADDING}px, ${getPos().top}px)`
				}}
				on:wheel={(e) => {
					e.stopPropagation();
					if (e.ctrlKey) {
						e.preventDefault();
					}
				}}
			>
				<StyleHeader />
				<div class="grid max-h-[467px] grid-cols-4 items-start justify-center gap-x-1 gap-y-3 overflow-y-auto">
					<For each={styles.data} fallback={<div>no data</div>}>
						{(item) => <StyleListItem {...item} />}
					</For>
				</div>
				<StyleFooter />
			</div>
		</StyleContext.Provider>
	);
};

/**
 * Component that renders the style header.
 */
const StyleHeader = () => {
	return (
		<div class="flex w-full items-center justify-between px-2">
			<div class="heading-24-semibold text-text-primary">Style</div>
			<CloseBtn />
		</div>
	);
};

/**
 * Component that renders the style footer.
 */
const StyleFooter = () => {
	return (
		<div class="flex w-full items-center justify-between">
			<WithoutStyleButton />
			<div />
		</div>
	);
};

/**
 * Component that renders the "Without style" button.
 */
const WithoutStyleButton = () => {
	const ctx = useCustomContext(StyleContext);

	const withoutStyleHandler = () => {
		EventAnalytics.styles.useWithout(ctx.globalId);
		deleteItem(ctx);
	};

	return (
		<Button size="small" variant="TertiaryOverlay" onClick={withoutStyleHandler}>
			Without style
		</Button>
	);
};

/**
 * Component that renders the "Apply style" button.
 */
const _ApplyStyleButton = () => {
	const ctx = useCustomContext(StyleContext);

	const applyStyleHandler = () => {
		const selected = styleMethods.getSelectedStyle(ctx);
		if (!selected) {
			return;
		}
		EventAnalytics.styles.applyStyle(ctx.globalId, selected.value);
		styleMethods.applyStyle({ ...ctx, style: selected });
		styleMethods.deleteItem(ctx);
	};

	return (
		<Button
			disabled={!styleMethods.getSelectedStyle(ctx)}
			class={twJoin(
				"rounded-xl",
				styleMethods.getSelectedStyle(ctx)
					? "bg-interaction-violet text-white"
					: "bg-background-gray-medium text-text-secondary"
			)}
			onClick={applyStyleHandler}
		>
			Apply
		</Button>
	);
};

/**
 * Component that renders the close button.
 */
const CloseBtn = () => {
	const ctx = useCustomContext(StyleContext);

	const delteHandler = () => {
		EventAnalytics.styles.close(ctx.globalId);
		deleteItem(ctx);
	};

	return (
		<Button size="medium" variant="SecondaryNeutralIcon" onClick={delteHandler}>
			<X size={16} />
		</Button>
	);
};

/**
 * Object containing methods for managing styles.
 */
export const styleMethods = {
	addStyleItem,
	getIsStyleSelected,
	deleteItem,
	selectStyle,
	applyStyle,
	getSelectedStyle,
	isNeedToOpenFirstInSessionStyles
};
